import React from 'react';
import styled from 'styled-components';

import { HolidayCount } from '../components/HolidayCount';
import type { AccountState } from './accountState';
import { useAuth } from '../firebase/FirebaseContext';
import { signOutUser } from '../auth/accountAuth';
import { useTotalLikedCount } from '../hooks/useTotalLikedCount';
import { AccountAlerts } from '../auth/AccountAlerts';
import { EmailVerificationNotice } from '../auth/EmailVerificationNotice';
import { useAlerts, type Alert } from '../hooks/useAlerts';
import { markSavedNativeBackSafe } from '../lib/savedBackMarker';

/**
 * The SIGNED-IN account surface, in the desktop avatar dropdown.
 *
 * "ON DESKTOP THE AVATAR *IS* THE YOU TAB" (accountState.ts / spec rule 4): the
 * signed-in dropdown shows the same content the /you page shows on desktop —
 * a notifications summary, "N saved across M people", identity, and Log out —
 * laid out for a dropdown rather than a page. `V4YouAccountCard` is its sibling
 * for the *anon* ask states (save-boards / emailed); it returns null when signed
 * in precisely because the account, not the ask, is what a signed-in user needs.
 *
 * Split into a PURE `V4AccountDropdownView` (all data as props → deterministic in
 * Storybook/Happo, unit-testable without Firebase) and a WIRED `V4AccountDropdown`
 * that reads the live saved-count + auth and hands them down. The wired wrapper
 * is mounted by `V4HomeAccountSlot` ONLY while a signed-in user has the dropdown
 * open, so its per-recipient Firestore listeners never spin up for the anon
 * homepage visitor.
 */

/* Anchored to the avatar's `position: relative` Slot, mirroring the Sent card's
   Popover in V4HomeAccountSlot. */
const Popover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 60;
  width: 268px;
  padding: 12px;
  border-radius: 16px;
  background: ${({ theme }) => theme.color.creamLight};
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  box-shadow: 0 16px 32px rgba(80, 40, 20, 0.16);
  font-family: ${({ theme }) => theme.font.sans};
  text-align: left;
`;

const IdentityHeader = styled.div`
  padding: 4px 8px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.color.warmBorder};
  margin-bottom: 8px;
  .name {
    font-size: 14px;
    font-weight: 600;
    color: hsl(var(--foreground));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .email {
    margin-top: 2px;
    font-size: 12px;
    color: ${({ theme }) => theme.color.mutedText};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SectionLabel = styled.div`
  padding: 0 8px;
  margin: 2px 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.mutedText};
`;

const Summary = styled.div`
  padding: 2px 8px 10px;
  font-size: 13.5px;
  color: hsl(var(--foreground));
`;

const SavedRow = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 14px;
  color: hsl(var(--foreground));
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.cream};
  }
  .chev {
    color: ${({ theme }) => theme.color.mutedText};
    font-size: 16px;
    line-height: 1;
  }
`;

const SavedEmpty = styled.div`
  padding: 2px 8px 10px;
  font-size: 13.5px;
  color: ${({ theme }) => theme.color.mutedText};
`;

const LogOutItem = styled.button`
  width: 100%;
  text-align: left;
  margin-top: 6px;
  padding: 8px;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.color.warmBorder};
  border-radius: 0 0 8px 8px;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  color: hsl(var(--foreground));
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.cream};
  }
`;

const SettingsItem = styled.a`
  display: block;
  padding: 8px;
  border-radius: 0 0 8px 8px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.color.mutedText};
  text-decoration: none;
  cursor: pointer;
  &:hover,
  &:focus-visible {
    color: hsl(var(--foreground));
    background: ${({ theme }) => theme.color.cream};
  }
`;

export interface V4AccountDropdownViewProps {
  verificationNotice?: React.ReactNode;
  holidayCount?: React.ReactNode;
  /** The account's display name, or null when it has none. */
  displayName: string | null;
  /** The account's email, or null. */
  email: string | null;
  /**
   * The materialised alert LIST (headline + per-recipient href + emoji), not a
   * count. The legacy HeaderAccountMenu showed the clickable, dismissable list;
   * v4 regressed to a bare count with no way to see or reach the notifications
   * (QA row 1). Restored by rendering the shared `AccountAlerts` here.
   */
  alerts: Alert[];
  /** Navigate to an alert's own destination (a per-recipient deep link). */
  onAlertClick: (alert: Alert) => void;
  /** Dismiss one alert by id. */
  onDismiss: (alertId: string) => void;
  /** Total saved items across every board (the "N" in "N saved across M"). */
  savedCount: number;
  /** How many boards / people (the "M"). */
  peopleCount: number;
  onLogOut: () => void;
  /** The all-saves board. Overridable for stories/tests; a plain anchor so the
   *  dropdown works when rendered outside a <Router> (header context). */
  savedHref?: string;
}

export const V4AccountDropdownView: React.FC<V4AccountDropdownViewProps> = ({
  verificationNotice,
  holidayCount = '20 on your calendar',
  displayName,
  email,
  alerts,
  onAlertClick,
  onDismiss,
  savedCount,
  peopleCount,
  onLogOut,
  savedHref = '/liked',
}) => {
  // Same identity chain as YouPage / HeaderAccountMenu: the email line only
  // shows when the name line came from a real displayName (else it would just
  // repeat the email).
  const name = displayName || email || 'Signed in';
  const showEmailLine = Boolean(displayName && email);

  return (
    <Popover role="menu" data-testid="v4-account-dropdown">
      <IdentityHeader data-testid="v4-account-identity">
        <div className="name">{name}</div>
        {showEmailLine ? <div className="email">{email}</div> : null}
      </IdentityHeader>

      {verificationNotice}
      <SectionLabel>Notifications</SectionLabel>
      {/* The clickable, dismissable list — the same shared component the legacy
          menu, the mobile board bell, and the You tab all use (QA row 1). Each
          row deep-links to its recipient board; × dismisses. AccountAlerts
          returns null when the list is empty, so the empty-state copy lives
          here under the same label. */}
      {alerts.length > 0 ? (
        <div data-testid="v4-account-notifications">
          <AccountAlerts
            variant="dropdown"
            alerts={alerts}
            showNudge={false}
            onAlertClick={onAlertClick}
            onDismiss={onDismiss}
          />
        </div>
      ) : (
        <Summary data-testid="v4-account-notifications">
          You&rsquo;re all caught up.
        </Summary>
      )}

      {savedCount > 0 ? (
        <SavedRow
          role="menuitem"
          href={savedHref}
          data-testid="v4-account-saved"
          // The all-saves row full-page-loads to /liked (this dropdown is
          // Router-independent). Arm the short-lived "native back is safe" marker
          // for a plain same-tab click to /liked so the page can history.back()
          // to here (with its scroll) instead of a fallback landing. The helper
          // ignores modified/middle clicks, a cancelled (defaultPrevented) click,
          // and any non-/liked destination, and the marker self-expires.
          onClick={markSavedNativeBackSafe}
        >
          <span>
            {savedCount} saved across {peopleCount}{' '}
            {peopleCount === 1 ? 'person' : 'people'}
          </span>
          <span className="chev" aria-hidden="true">
            ›
          </span>
        </SavedRow>
      ) : (
        <SavedEmpty data-testid="v4-account-saved-empty">Nothing saved yet.</SavedEmpty>
      )}

      <SectionLabel>Holidays</SectionLabel>
      <SavedRow role="menuitem" href="/holidays" aria-label="Holidays">
        <span>{holidayCount}</span><span className="chev" aria-hidden="true">›</span>
      </SavedRow>

      <LogOutItem
        type="button"
        role="menuitem"
        data-testid="v4-account-logout"
        onClick={onLogOut}
      >
        Log out
      </LogOutItem>

      {/* Settings links to the /you account page, which owns account deletion
          (Manny, 2026-09-02: /settings and /you were redundant — consolidated
          to /you). The dialog can't live in this popover: a modal rendered here
          is unmounted by the host's outside-click handler the moment the user
          reaches for it — so the destructive control lives on /you, a surface
          that can hold it. Subordinate styling on purpose. */}
      <SettingsItem role="menuitem" href="/you" data-testid="v4-account-settings">
        Settings
      </SettingsItem>
    </Popover>
  );
};

export interface V4AccountDropdownProps {
  state: AccountState;
  /** Close the dropdown (after Log out, or the host's Esc / outside-click). */
  onClose: () => void;
}

export const V4AccountDropdown: React.FC<V4AccountDropdownProps> = ({ state, onClose }) => {
  const auth = useAuth();
  // Both live here (not in the always-mounted door) so the per-recipient
  // listeners + the alert listener only start when a signed-in user actually
  // opens the menu.
  const savedCount = useTotalLikedCount();
  const { alerts, dismiss } = useAlerts();

  const handleLogOut = async () => {
    try {
      await signOutUser(auth);
    } catch (err) {
      // Non-fatal: the auth listener still reconciles the signed-out state.
      console.error('[V4AccountDropdown] signOut failed:', err);
    }
    onClose();
  };

  return (
    <V4AccountDropdownView
      holidayCount={<HolidayCount />}
      verificationNotice={<EmailVerificationNotice user={auth.currentUser} />}
      displayName={state.displayName ?? null}
      email={state.accountEmail ?? null}
      alerts={alerts}
      // The dropdown renders outside a <Router> (header context), so navigate
      // with a plain assign — mirrors savedHref's anchor and HeaderAccountMenu.
      onAlertClick={(a) => window.location.assign(a.href)}
      onDismiss={dismiss}
      savedCount={savedCount}
      peopleCount={state.boards.length}
      onLogOut={handleLogOut}
    />
  );
};

export default V4AccountDropdown;
